import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Download, FileBadge, Printer, ScrollText } from 'lucide-react';
import { Button, FormField, Modal, Skeleton, Tabs } from '@/components/ui';
import { Input } from '@/components/ui/input';
import type { PdfMode } from '@/lib/pdf';
import { getApiErrorMessage } from '@/lib/axios';
import {
  type EmployeDTO,
  type ModeleDocumentRH,
  type TypeDocumentRH,
  emettreDocument,
  getApercuDocument,
  getModelesDocuments,
  listDocumentsEmis,
  openDocumentRhPdf,
  updateModeleDocument,
} from '@/services/rh';
import { toast } from '@/store/toastStore';

const TYPE_LABEL: Record<TypeDocumentRH, string> = {
  ATTESTATION_TRAVAIL: 'Attestation de travail',
  CERTIFICAT_TRAVAIL: 'Certificat de travail',
};
const PLACEHOLDERS = ['nom_complet', 'poste', 'type_contrat', 'date_naissance', 'lieu_naissance', 'nin', 'matricule', 'date_embauche', 'date_sortie', 'entreprise'];
const textareaCls = 'min-h-[230px] w-full resize-y rounded-md border border-border bg-surface-primary p-3 text-sm leading-relaxed text-content-primary';

/* ============ Génération depuis la fiche employé (attestation / certificat) ============ */
export function DocumentsEmployeModal({ employe, open, onOpenChange }: { employe: EmployeDTO | null; open: boolean; onOpenChange: (v: boolean) => void }) {
  const queryClient = useQueryClient();
  const [type, setType] = useState<TypeDocumentRH | null>(null);
  const [titre, setTitre] = useState('');
  const [signataire, setSignataire] = useState('');
  const [lieu, setLieu] = useState('');
  const [corps, setCorps] = useState('');

  // Éligibilité (miroir du serveur) : attestation = en poste ; certificat = fin de contrat.
  const attEligible = !!employe && employe.statut !== 'SORTI';
  const crtEligible = !!employe && (employe.statut === 'SORTI' || !!employe.date_fin_contrat);

  useEffect(() => { if (!open) { setType(null); setCorps(''); } }, [open]);

  const apercuMut = useMutation({
    mutationFn: (t: TypeDocumentRH) => getApercuDocument(employe!.id, t),
    onSuccess: (ap) => { setType(ap.type); setTitre(ap.titre); setSignataire(ap.signataire); setLieu(ap.lieu ?? ''); setCorps(ap.corps); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const histo = useQuery({ queryKey: ['documents-rh', employe?.id], queryFn: () => listDocumentsEmis(employe!.id), enabled: !!employe && open });

  const genMut = useMutation({
    mutationFn: async (mode: PdfMode) => {
      const doc = await emettreDocument(employe!.id, { type: type!, titre, corps, signataire, lieu: lieu.trim() || null });
      await openDocumentRhPdf(doc.id, mode);
      return doc;
    },
    onSuccess: (doc) => { toast.success(`Document ${doc.numero} généré et tracé.`); queryClient.invalidateQueries({ queryKey: ['documents-rh', employe?.id] }); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const choix = (t: TypeDocumentRH, eligible: boolean) => (
    <button
      key={t}
      disabled={!eligible || apercuMut.isPending}
      onClick={() => apercuMut.mutate(t)}
      className={`flex flex-1 items-center gap-3 rounded-lg border p-3 text-left transition ${type === t ? 'border-brand-primary bg-brand-primary/5' : 'border-border bg-surface-secondary hover:bg-surface-hover'} ${!eligible ? 'cursor-not-allowed opacity-50' : ''}`}
    >
      <span className="flex size-9 items-center justify-center rounded-md bg-brand-primary/10 text-brand-primary">
        {t === 'ATTESTATION_TRAVAIL' ? <FileBadge className="size-5" /> : <ScrollText className="size-5" />}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-content-primary">{TYPE_LABEL[t]}</span>
        <span className="block text-xs text-content-muted">
          {t === 'ATTESTATION_TRAVAIL' ? 'Employé en poste' : 'Fin de contrat / départ'}
          {!eligible && (t === 'ATTESTATION_TRAVAIL' ? ' — employé sorti' : ' — pas de fin de contrat')}
        </span>
      </span>
    </button>
  );

  return (
    <Modal open={open} onOpenChange={onOpenChange} title={employe ? `Documents — ${employe.prenom} ${employe.nom}` : 'Documents'} size="lg">
      <div className="space-y-4">
        <div className="flex flex-col gap-2 sm:flex-row">
          {choix('ATTESTATION_TRAVAIL', attEligible)}
          {choix('CERTIFICAT_TRAVAIL', crtEligible)}
        </div>

        {apercuMut.isPending && <Skeleton className="h-48 w-full" />}

        {type && !apercuMut.isPending && (
          <div className="space-y-3 rounded-lg border border-border bg-surface-secondary p-4">
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-content-secondary">
              <AlertTriangle className="mr-1 inline size-3.5 text-amber-600" />
              Texte issu du <span className="font-medium">modèle éditable</span> (bouton « Modèles de documents »). Vérifiez/ajustez le contenu légal — il est sous votre responsabilité — avant de générer.
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <FormField label="Titre" className="sm:col-span-2"><Input value={titre} onChange={(e) => setTitre(e.target.value)} /></FormField>
              <FormField label="Lieu (Fait à…)"><Input value={lieu} onChange={(e) => setLieu(e.target.value)} placeholder="ex : Oran" /></FormField>
            </div>
            <FormField label="Corps du document (modifiable)">
              <textarea className={textareaCls} value={corps} onChange={(e) => setCorps(e.target.value)} />
            </FormField>
            <FormField label="Signataire"><Input value={signataire} onChange={(e) => setSignataire(e.target.value)} placeholder="Le Gérant" /></FormField>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={() => genMut.mutate('open')} loading={genMut.isPending} disabled={!corps.trim()}><Printer /> Générer & imprimer</Button>
              <Button onClick={() => genMut.mutate('download')} loading={genMut.isPending} disabled={!corps.trim()}><Download /> Générer & télécharger</Button>
            </div>
          </div>
        )}

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-muted">Historique des documents émis</p>
          {histo.isLoading ? <Skeleton className="h-16 w-full" /> : (histo.data ?? []).length === 0 ? (
            <p className="text-sm text-content-muted">Aucun document émis pour cet employé.</p>
          ) : (
            <div className="space-y-1.5">
              {(histo.data ?? []).map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface-secondary px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <span className="font-medium text-content-primary">{d.numero}</span>
                    <span className="ml-2 text-content-secondary">{TYPE_LABEL[d.type]}</span>
                    <span className="ml-2 text-xs text-content-muted">{d.date_emission}{d.emis_par_nom ? ` · ${d.emis_par_nom}` : ''}</span>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDocumentRhPdf(d.id, 'open').catch((e) => toast.error(getApiErrorMessage(e)))} aria-label="Imprimer"><Printer className="size-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDocumentRhPdf(d.id, 'download').catch((e) => toast.error(getApiErrorMessage(e)))} aria-label="Télécharger"><Download className="size-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

/* ============ Édition des modèles (texte légal paramétrable) ============ */
export function ModelesDocumentsModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['modeles-documents'], queryFn: getModelesDocuments, enabled: open });
  const [vue, setVue] = useState<TypeDocumentRH>('ATTESTATION_TRAVAIL');
  const [edits, setEdits] = useState<Record<string, ModeleDocumentRH>>({});

  useEffect(() => {
    if (data) setEdits(Object.fromEntries(data.map((m) => [m.type, { ...m }])));
  }, [data]);

  const courant = edits[vue];
  const setChamp = (champ: keyof ModeleDocumentRH, valeur: string) =>
    setEdits((e) => ({ ...e, [vue]: { ...e[vue], [champ]: valeur } }));

  const saveMut = useMutation({
    mutationFn: () => updateModeleDocument(vue, { titre: courant.titre, corps: courant.corps, signataire: courant.signataire, lieu: courant.lieu }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['modeles-documents'] }); toast.success('Modèle enregistré.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <Modal open={open} onOpenChange={onOpenChange} title="Modèles de documents RH" size="lg"
      footer={courant ? <><Button variant="secondary" onClick={() => onOpenChange(false)}>Fermer</Button><Button onClick={() => saveMut.mutate()} loading={saveMut.isPending}>Enregistrer le modèle</Button></> : undefined}>
      <Tabs className="mb-4" value={vue} onChange={(v) => setVue(v as TypeDocumentRH)}
        tabs={[{ key: 'ATTESTATION_TRAVAIL', label: 'Attestation' }, { key: 'CERTIFICAT_TRAVAIL', label: 'Certificat' }]} />

      {isLoading || !courant ? <Skeleton className="h-64 w-full" /> : (
        <div className="space-y-3">
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-content-secondary">
            <AlertTriangle className="mr-1 inline size-3.5 text-amber-600" />
            Modèle de départ <span className="font-medium">à faire valider par votre conseil RH/comptable</span>, puis à ajuster ici. Le numéro, la date et l'en-tête (logo, RC/NIF/NIS/AI) sont ajoutés automatiquement à la génération.
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <FormField label="Titre du document" className="sm:col-span-2"><Input value={courant.titre} onChange={(e) => setChamp('titre', e.target.value)} /></FormField>
            <FormField label="Lieu par défaut"><Input value={courant.lieu ?? ''} onChange={(e) => setChamp('lieu', e.target.value)} placeholder="ex : Oran" /></FormField>
          </div>
          <FormField label="Corps (texte type)">
            <textarea className={textareaCls} value={courant.corps} onChange={(e) => setChamp('corps', e.target.value)} />
          </FormField>
          <FormField label="Signataire"><Input value={courant.signataire} onChange={(e) => setChamp('signataire', e.target.value)} /></FormField>
          <div className="text-xs text-content-muted">
            <span className="font-medium text-content-secondary">Champs fusionnés :</span>{' '}
            {PLACEHOLDERS.map((p) => <code key={p} className="mr-1 rounded bg-surface-hover px-1 py-0.5">{`{{${p}}}`}</code>)}
          </div>
        </div>
      )}
    </Modal>
  );
}
